import { useCallback, useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';

import NotificationCard from './NotificationCard';
import { useUser } from 'global/UserContext';
import { useWebSocket } from 'global/WebSocketContext';
import { useNotificationTooltip } from 'hooks/useNotificationTooltip';
import { apiNotifications } from 'services/apiService';
import { Notification } from 'types/notificationsTypes';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

interface NotificationsProps {
  open: boolean;
  onClose: () => void;
  onNewNotification: (newNotifications: number) => void;
  onOpenChange?: (isOpen: boolean) => void;
}

interface GroupedNotifications {
  recent: Notification[];
  yesterday: Notification[];
  lastWeek: Notification[];
  older: Notification[];
}

const Notifications = ({ open, onClose, onNewNotification, onOpenChange }: NotificationsProps) => {
  const [newNotifications, setNewNotifications] = useState(0);
  const { user } = useUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const { lastMessage, isConnected, sendMessage } = useWebSocket();
  const lastProcessedMessageIdRef = useRef<string | null>(null);
  const isInitialLoadCompleteRef = useRef(false);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const [wsNotificationCount, setWsNotificationCount] = useState(0); // Separate state for WebSocket notifications

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDrawer = isMobile || isTablet;

  // Hook para gerenciar tooltip de notificações (usado apenas para sincronização)
  const { showTooltip, tooltipNotification, handleTooltipClose } = useNotificationTooltip();

  // Function to group notifications by time periods
  const groupNotificationsByTime = (notifications: Notification[]): GroupedNotifications => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    const grouped: GroupedNotifications = {
      recent: [],
      yesterday: [],
      lastWeek: [],
      older: [],
    };

    notifications.forEach(notification => {
      const sentDate = new Date(notification.sent_at);
      const sentDateOnly = new Date(
        sentDate.getFullYear(),
        sentDate.getMonth(),
        sentDate.getDate()
      );

      if (sentDateOnly.getTime() === today.getTime()) {
        grouped.recent.push(notification);
      } else if (sentDateOnly.getTime() === yesterday.getTime()) {
        grouped.yesterday.push(notification);
      } else if (sentDateOnly >= lastWeekStart && sentDateOnly < yesterday) {
        grouped.lastWeek.push(notification);
      } else {
        grouped.older.push(notification);
      }
    });

    return grouped;
  };

  // Function to render notification group
  const renderNotificationGroup = (title: string, notifications: Notification[]) => {
    if (notifications.length === 0) return null;

    return (
      <Box key={title} sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            ml: '5%',
            color: 'text.primary',
            fontSize: '0.9rem',
          }}
        >
          {title}
        </Typography>
        {notifications.map((notification: any) => (
          <NotificationCard key={notification.id} notification={notification} isDrawer={isDrawer} />
        ))}
        <Divider sx={{ mt: 2 }} />
      </Box>
    );
  };

  // Function to fetch notifications
  const fetchNotifications = useCallback(
    async (pageNum: number, append: boolean = false) => {
      if (!user?.id || isLoadingRef.current) return;

      isLoadingRef.current = true;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await apiNotifications.get(
          `/v1/notifications/user/${user?.id}?size=${size}&page=${pageNum}`
        );
        const newNotificationsData = response.data.items;

        if (append) {
          setNotifications(prev => [...prev, ...newNotificationsData]);
        } else {
          setNotifications(newNotificationsData);
          lastProcessedMessageIdRef.current = newNotificationsData[0]?.id || null;
        }

        // Check if there are more notifications to load
        setHasMore(newNotificationsData.length === size);

        // Handle unseen notifications count (only for initial load)
        if (!append) {
          const unseenNotifications = newNotificationsData.filter(
            (notification: Notification) => !notification.seen
          );
          console.log('[Notifications] Unseen notifications:', unseenNotifications);
          onNewNotification(unseenNotifications.length);
          setNewNotifications(unseenNotifications.length);
        }
      } catch (error) {
        console.error('[Notifications] Error fetching notifications:', error);
      } finally {
        isLoadingRef.current = false;
        if (append) {
          setIsLoadingMore(false);
        } else {
          setLoading(false);
        }
        setInitialLoad(false);
      }
    },
    [user?.id, size, onNewNotification]
  );

  // Handle scroll to detect when to load more
  const handleScroll = useCallback(() => {
    if (!contentRef.current || isLoadingRef.current || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80% of the content
    if (scrollPercentage > 0.8) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage, true);
    }
  }, [hasMore, page, fetchNotifications]);

  // Initial load
  useEffect(() => {
    if (!user?.id) return;

    setPage(1);
    setHasMore(true);
    setInitialLoad(true);
    setIsLoadingMore(false);
    isLoadingRef.current = false;
    fetchNotifications(1, false);
  }, [user?.id, fetchNotifications]);

  // Mark initial load as complete after first fetch
  useEffect(() => {
    if (!initialLoad && notifications.length > 0) {
      isInitialLoadCompleteRef.current = true;
      // Set the last processed message ID to the most recent notification
      if (notifications[0]?.id) {
        lastProcessedMessageIdRef.current = notifications[0].id;
      }
    }
  }, [initialLoad, notifications]);

  // WebSocket effect for real-time notifications
  useEffect(() => {
    if (!isConnected) {
      console.log('[Notifications] Not connected');
      return;
    }
    if (!lastMessage) return;

    // Don't process WebSocket messages until initial load is complete
    if (!isInitialLoadCompleteRef.current) {
      console.log('[Notifications] Initial load not complete, ignoring WebSocket message');
      return;
    }

    // Only process if this is a new message we haven't seen before
    if (lastMessage.id !== lastProcessedMessageIdRef.current) {
      console.log('[Notifications] Processing new WebSocket message:', lastMessage.id);
      lastProcessedMessageIdRef.current = lastMessage.id;

      setNewNotifications(prev => prev + 1);
      onNewNotification(newNotifications + 1);
      setNotifications(prev => [lastMessage, ...prev]);

      console.log('[Notifications] New notification received:', lastMessage);
    } else {
      console.log('[Notifications] Duplicate WebSocket message ignored:', lastMessage.id);
    }
  }, [isConnected, lastMessage, newNotifications, onNewNotification]);

  // Effect to update the parent component with the total count
  useEffect(() => {
    const totalCount = newNotifications + wsNotificationCount;
    onNewNotification(totalCount);
  }, [newNotifications, wsNotificationCount, onNewNotification]);

  // Mark notifications as seen when dialog opens
  useEffect(() => {
    if (open && notifications.length > 0) {
      const notificationIds = notifications.map(n => n.id);
      sendMessage({
        action: 'MARK_AS_SEEN',
        data: { notification_ids: notificationIds },
      });
      onNewNotification(0);
    }
    // Only run when dialog opens or notifications change
  }, [open, notifications, sendMessage]);

  // Notify parent component about open/close state changes
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const handleMarkAllAsRead = () => {
    const notificationIds = notifications.map(n => n.id);
    sendMessage({
      action: 'MARK_AS_READ',
      data: { notification_ids: notificationIds },
    });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const groupedNotifications = groupNotificationsByTime(notifications);
  const hasAnyNotifications = Object.values(groupedNotifications).some(group => group.length > 0);

  // Conteúdo principal (usado tanto no Drawer quanto no Dialog)
  const MainContent = (
    <Box
      ref={contentRef}
      onScroll={handleScroll}
      sx={{
        padding: 0,
        maxHeight: isDrawer ? '100vh' : '90vh',
        overflowY: 'auto',
        height: isDrawer ? '100vh' : '100%',
        minWidth: isDrawer ? 320 : 400,
      }}
    >
      {initialLoad ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : hasAnyNotifications ? (
        <Box paddingTop={2}>
          <Box display="flex" justifyContent="flex-end" paddingRight={2} sx={{ height: '0px' }}>
            <Typography
              variant="body2"
              color="primary.main"
              onClick={handleMarkAllAsRead}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Marcar todas como lidas
            </Typography>
          </Box>
          {renderNotificationGroup('Recentes', groupedNotifications.recent)}
          {renderNotificationGroup('Ontem', groupedNotifications.yesterday)}
          {renderNotificationGroup('Semana passada', groupedNotifications.lastWeek)}
          {renderNotificationGroup('Mais antigas', groupedNotifications.older)}

          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && (
            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* End of notifications indicator */}
          {!hasMore && notifications.length > 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="text.secondary">
                Não há mais notificações
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary" textAlign="center" padding={2}>
          Não há notificações
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      {isDrawer ? (
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              width: '100vw',
              height: '100vh',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              bgcolor: 'background.default',
              boxShadow: 6,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              opacity: 0.97,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconButton onClick={onClose} size="large" sx={{ alignSelf: 'flex-end' }}>
              <CloseIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 24 }}
            >
              Notificações
            </Typography>
          </Box>
          {MainContent}
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          aria-labelledby="notifications-dialog-title"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              // maxWidth: 400,
              borderRadius: 3,
              p: 0,
              m: 0,
              backgroundColor: 'background.default',
              height: '100%',
              overflowY: 'hidden',
            },
          }}
        >
          <DialogTitle
            id="notifications-dialog-title"
            sx={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 20 }}
          >
            Notificações
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ width: '100%', height: '100%', p: 0, m: 0 }}>
            {MainContent}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default Notifications;
